import axios from "axios";
import { toast } from "react-toastify";
import { dataURLtoBlob } from "./imageUtils";

/**
 * Initialize Facebook SDK
 * @returns {Promise} - Promise that resolves when FB SDK is ready
 */
export const initFacebookSDK = () => {
  return new Promise((resolve) => {
    if (window.FB) {
      resolve();
    } else {
      window.fbAsyncInit = () => {
        resolve();
      };
    }
  });
};

/**
 * Upload an image to Facebook
 * @param {string|Blob} image - Image URL, base64 string, or Blob
 * @param {Object} fanpage - Fanpage object (optional)
 * @returns {Promise<string>} - Promise that resolves with the photo ID
 */
export const uploadImage = (image, fanpage) => {
  // Lấy access token từ fanpage hoặc từ localStorage nếu không có fanpage
  const accessToken = fanpage
    ? fanpage.access_token
    : localStorage.getItem("fbAccessToken");
  const endpoint = fanpage
    ? `https://graph.facebook.com/${fanpage.id}/photos`
    : "https://graph.facebook.com/me/photos";

  return new Promise((resolve, reject) => {
    if (typeof image === "string" && image.startsWith("http")) {
      // Kiểm tra ảnh URL có truy cập được không
      const img = new Image();
      img.onload = () => {
        // Nếu ảnh tải được, tiến hành upload
        const formData = new FormData();
        formData.append("url", image);
        formData.append("published", "false");
        formData.append("access_token", accessToken);

        axios
          .post(endpoint, formData)
          .then((response) => {
            resolve(response.data.id);
          })
          .catch((error) => {
            let errorMessage = "Ảnh được chọn bị lỗi, vui lòng chọn ảnh khác";
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 3000,
            });
            reject(error);
          });
      };

      img.onerror = () => {
        toast.error(
          "Không thể truy cập URL hình ảnh. Vui lòng thử lại với ảnh khác.",
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
        reject(new Error("Không thể truy cập URL hình ảnh"));
      };

      img.src = image;
    } else if (typeof image === "string" && image.startsWith("data:image")) {
      // Xử lý ảnh base64 từ Gemini - CẢI TIẾN cho thiết bị di động
      try {
        // Chuyển data URL thành Blob
        const imageBlob = dataURLtoBlob(image);

        // Kiểm tra kích thước Blob (nếu quá lớn có thể gây lỗi trên mobile)
        const blobSizeMB = imageBlob.size / (1024 * 1024);

        // Nếu ảnh quá lớn, thông báo cho người dùng nhưng vẫn thử tải lên
        if (blobSizeMB > 4) {
          console.warn(
            "Ảnh từ AI có kích thước lớn, có thể gặp vấn đề khi tải lên"
          );
        }

        // Tạo formData với ảnh dạng Blob
        const formData = new FormData();
        formData.append("source", imageBlob);
        formData.append("published", "false");
        formData.append("access_token", accessToken);

        // Tăng timeout cho request trên thiết bị di động
        axios
          .post(endpoint, formData, {
            timeout: 30000, // Tăng timeout lên 30 giây
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data.id);
          })
          .catch((error) => {
            console.error("Lỗi khi upload ảnh Gemini:", error);

            // Thử lại một lần nữa khi gặp lỗi
            console.log("Thử lại upload ảnh...");
            setTimeout(() => {
              axios
                .post(endpoint, formData, {
                  timeout: 60000, // Tăng timeout lên 60 giây cho lần thử lại
                })
                .then((response) => {
                  resolve(response.data.id);
                })
                .catch((retryError) => {
                  console.error(
                    "Lỗi khi thử lại upload ảnh Gemini:",
                    retryError
                  );
                  let errorMessage =
                    "Không thể đăng ảnh từ Gemini lên Facebook. Vui lòng thử lại sau.";
                  toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                  });
                  reject(retryError);
                });
            }, 2000); // Đợi 2 giây trước khi thử lại
          });
      } catch (error) {
        console.error("Lỗi khi xử lý ảnh base64 từ Gemini:", error);
        toast.error("Không thể xử lý ảnh từ Gemini", {
          position: "top-right",
          autoClose: 3000,
        });
        reject(error);
      }
    } else {
      // Xử lý cho trường hợp image là Blob hoặc các dạng khác
      try {
        // Tạo Blob từ image nếu nó chưa phải là Blob
        const blob = image instanceof Blob ? image : new Blob([image]);

        const formData = new FormData();
        formData.append("source", blob, "image.jpg");
        formData.append("published", "false");
        formData.append("access_token", accessToken);

        axios
          .post(endpoint, formData)
          .then((response) => {
            resolve(response.data.id);
          })
          .catch((error) => {
            let errorMessage = "Ảnh được chọn bị lỗi, vui lòng chọn ảnh khác";
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 3000,
            });
            reject(error);
          });
      } catch (error) {
        console.error("Lỗi khi xử lý ảnh:", error);
        toast.error("Không thể xử lý ảnh. Vui lòng thử lại.", {
          position: "top-right",
          autoClose: 3000,
        });
        reject(error);
      }
    }
  });
};
